import { expandCollapsibleAncestors } from './collapsibleSection';
import { requiredFieldError } from './validationI18n';

export type RequiredFieldSpec = {
  key: string;
  label: string;
  value: unknown;
  elementId: string;
};

export type RequiredErrors = Record<string, string>;

function isBlank(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

export function validateRequired(specs: RequiredFieldSpec[]): RequiredErrors {
  const errs: RequiredErrors = {};
  for (const s of specs) {
    if (isBlank(s.value)) errs[s.key] = requiredFieldError(s.label);
  }
  return errs;
}

function focusElement(el: HTMLElement): void {
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch {
    el.scrollIntoView();
  }
  window.setTimeout(() => {
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
      el.focus();
      return;
    }
    const nested = el.querySelector('input,select,textarea,button,[tabindex]') as HTMLElement | null;
    nested?.focus?.();
  }, 50);
}

function resolveElementForError(firstKey: string, specs: RequiredFieldSpec[]): HTMLElement | null {
  const spec = specs.find((s) => s.key === firstKey);
  if (spec?.elementId) {
    const byId = document.getElementById(spec.elementId);
    if (byId) return byId;
  }
  return document.getElementById(firstKey);
}

export function focusFirstInvalid(specs: RequiredFieldSpec[], errs: RequiredErrors): void {
  const firstKey = specs.find((s) => Boolean(errs[s.key]))?.key ?? Object.keys(errs)[0];
  if (!firstKey) return;

  const el = resolveElementForError(firstKey, specs);
  if (!el) return;

  expandCollapsibleAncestors(el);
  window.requestAnimationFrame(() => {
    focusElement(el);
  });
}
